import React from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import { InferType } from 'yup'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { Button, ButtonVariant } from '../../../../components/common/Button'
import { TextInput } from '../../../../components/common/TextInput'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useFormErrorMessage } from '../../../../hooks/useFormErrorMessage'
import { theme } from '../../../../theme/theme'
import { ToastType } from '../../../../components/common/Toast/toast-type'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { FormControl } from '../../../../components/common/FormControl'
import { resetPasswordSchema } from '../../../../forms/schemas/reset_password'
import { sendResetPassword } from '../../../../api/users'
import { isRateLimited } from '../../../../helpers/rate_limit'

interface ResetPasswordFormProps {
  setResetPasswordEmailSent: (email: string) => void
}

export const ResetPasswordForm = ({
  setResetPasswordEmailSent,
}: ResetPasswordFormProps): React.ReactNode => {
  const navigate = useNavigate()
  const { translate } = useAppTranslation()
  const { getErrorMessage } = useFormErrorMessage()
  const { setToastNotification } = useToastNotifications()

  const getDefaultValues = (): InferType<typeof resetPasswordSchema> => {
    return {
      email: '',
    }
  }

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: yupResolver(resetPasswordSchema),
    defaultValues: getDefaultValues(),
  })

  const { mutate: mutateResetPassword, isPending: isResetingPassword } =
    useMutation({
      mutationFn: async (variables: { email: string }) => {
        // Deliberately no check for whether the address is registered. Doing
        // so let anyone use this form to find out which addresses have a
        // Boappa account, so the flow now looks identical either way.
        await sendResetPassword(variables.email.toLowerCase())
      },
      onSuccess: (_data, variables) => {
        setToastNotification(
          ToastType.SUCCESS,
          translate(
            'authenticationResetPasswordView.flashMessage.passwordResetSuccess'
          )
        )
        setResetPasswordEmailSent(variables.email.toLowerCase())
      },
      // A limit turned the request away rather than anything going wrong, and
      // the two need opposite words: "try again shortly" against "the password
      // could not be reset", which was shown for both and is untrue of the
      // first -- nothing was reset, a mail was asked for.
      onError: (error) => {
        if (isRateLimited(error)) {
          setToastNotification(
            ToastType.DANGER,
            translate('flashMessage.tooManyAttemptsError')
          )
          return
        }

        setToastNotification(
          ToastType.DANGER,
          translate(
            'authenticationResetPasswordView.flashMessage.passwordResetFailure'
          )
        )
      },
    })

  const onSubmit = (data: InferType<typeof resetPasswordSchema>): void => {
    const { email } = data
    mutateResetPassword({ email })
  }

  const onError = (): void => {
    if (!isValid) return
    setToastNotification(
      ToastType.DANGER,
      translate('flashMessage.somethingWentWrongError')
    )
  }

  const loading = isResetingPassword

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      className="flex flex-col gap-4"
    >
      <Controller
        control={control}
        name="email"
        render={({ field: { value, name, onChange, onBlur } }) => (
          <FormControl
            error={errors.email && getErrorMessage(errors.email)}
            name={name}
            label={translate(
              'authenticationResetPasswordView.form.labels.email'
            )}
          >
            <TextInput
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              type="email"
              error={!!errors.email}
            />
          </FormControl>
        )}
      />
      <div className="mt-4 flex flex-col gap-4 md:mt-12">
        <Button
          type="submit"
          style={{
            ...theme.textVariants.paragraph,
          }}
          className="flex h-14 w-full items-center justify-center"
          label={translate(
            'authenticationResetPasswordView.resetPasswordButtonTitle'
          )}
          onClick={handleSubmit(onSubmit, onError)}
          loading={loading}
          variant={ButtonVariant.PRIMARY}
        />
        <Button
          style={{
            ...theme.textVariants.paragraph,
          }}
          className="flex h-14 w-full items-center justify-center"
          label={translate('common.actions.cancel')}
          onClick={() => navigate(-1)}
          disabled={loading}
        />
      </div>
    </form>
  )
}
